/**
 * AdminTour — Renders the active tour from TourContext.
 *
 * Mounted ONCE in the AdminLayout. Reads `steps` and `isOpen` from context.
 * Pages declare their tour via useRegisterTour(steps, pageKey).
 *
 * The "?" launch button lives in the AdminLayout header.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { X, ArrowLeft, ArrowRight } from 'lucide-react';
import { useTour } from '../../context/TourContext';
import useAdminT from '../../hooks/useAdminT';

const SPOTLIGHT_PADDING = 8;
const TOOLTIP_OFFSET = 16;
const TOOLTIP_WIDTH = 380;
const TOOLTIP_MAX_HEIGHT = 360;

function clamp(v, min, max) {
  return Math.max(min, Math.min(max, v));
}

const AdminTour = () => {
  const { steps, isOpen, close } = useTour();
  const aT = useAdminT();
  const t = aT.tour || {};

  const [stepIdx, setStepIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0, placement: 'bottom' });
  const tooltipRef = useRef(null);

  const finish = useCallback(() => {
    close(true);
    setStepIdx(0);
  }, [close]);

  // Reset to step 0 when tour opens
  useEffect(() => {
    if (isOpen) setStepIdx(0);
  }, [isOpen]);

  const currentStep = steps?.[stepIdx];

  // Close on Escape, navigate with arrows
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') finish();
      else if (e.key === 'ArrowRight' || e.key === 'Enter') {
        if (stepIdx < (steps?.length || 0) - 1) setStepIdx(stepIdx + 1);
        else finish();
      } else if (e.key === 'ArrowLeft' && stepIdx > 0) setStepIdx(stepIdx - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, stepIdx, steps, finish]);

  // Compute target rect + tooltip position
  useEffect(() => {
    if (!isOpen || !currentStep) return;
    const compute = (retried = false) => {
      const el = document.querySelector(currentStep.target);
      if (!el) {
        // Element not in DOM — center tooltip and skip spotlight
        setRect(null);
        setTooltipPos({
          top: window.innerHeight / 2 - 100,
          left: window.innerWidth / 2 - TOOLTIP_WIDTH / 2,
          placement: 'center',
        });
        return;
      }
      const r = el.getBoundingClientRect();
      const inView = r.top >= 0 && r.bottom <= window.innerHeight && r.left >= 0 && r.right <= window.innerWidth;
      if (!inView && !retried) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
        setTimeout(() => compute(true), 400);
        return;
      }
      setRect({
        top: r.top - SPOTLIGHT_PADDING,
        left: r.left - SPOTLIGHT_PADDING,
        width: r.width + SPOTLIGHT_PADDING * 2,
        height: r.height + SPOTLIGHT_PADDING * 2,
      });
      const placement = currentStep.placement || 'bottom';
      let top, left;
      const cw = window.innerWidth;
      const ch = window.innerHeight;
      switch (placement) {
        case 'top':
          top = r.top - TOOLTIP_MAX_HEIGHT - TOOLTIP_OFFSET;
          left = r.left + r.width / 2 - TOOLTIP_WIDTH / 2;
          break;
        case 'right':
          top = r.top + r.height / 2 - 120;
          left = r.right + TOOLTIP_OFFSET;
          break;
        case 'left':
          top = r.top + r.height / 2 - 120;
          left = r.left - TOOLTIP_WIDTH - TOOLTIP_OFFSET;
          break;
        case 'bottom':
        default:
          top = r.bottom + TOOLTIP_OFFSET;
          left = r.left + r.width / 2 - TOOLTIP_WIDTH / 2;
      }
      left = clamp(left, 12, cw - TOOLTIP_WIDTH - 12);
      top = clamp(top, 12, ch - TOOLTIP_MAX_HEIGHT - 12);
      setTooltipPos({ top, left, placement });
    };
    compute();
    window.addEventListener('resize', compute);
    window.addEventListener('scroll', compute, true);
    return () => {
      window.removeEventListener('resize', compute);
      window.removeEventListener('scroll', compute, true);
    };
  }, [isOpen, currentStep]);

  if (!isOpen || !steps || steps.length === 0 || !currentStep) return null;

  return (
    <div className="fixed inset-0 z-[10000] pointer-events-none">
      {/* SVG dim overlay with spotlight cutout */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-auto"
        style={{ width: '100vw', height: '100vh' }}
        onClick={() => finish()}
      >
        <defs>
          <mask id="spotlight-mask">
            <rect width="100%" height="100%" fill="white" />
            {rect && (
              <rect
                x={rect.left}
                y={rect.top}
                width={rect.width}
                height={rect.height}
                rx="8"
                ry="8"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill="rgba(0, 0, 0, 0.65)"
          mask="url(#spotlight-mask)"
        />
        {rect && (
          <rect
            x={rect.left}
            y={rect.top}
            width={rect.width}
            height={rect.height}
            rx="8"
            ry="8"
            fill="none"
            stroke="#E87722"
            strokeWidth="2.5"
            style={{ pointerEvents: 'none' }}
          />
        )}
      </svg>

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className="absolute bg-white rounded-xl shadow-2xl pointer-events-auto"
        style={{
          top: tooltipPos.top,
          left: tooltipPos.left,
          width: TOOLTIP_WIDTH,
          maxHeight: TOOLTIP_MAX_HEIGHT,
          overflowY: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5">
          {/* Header */}
          <div className="flex items-start justify-between gap-3 mb-3">
            {currentStep.title && (
              <h3 className="font-semibold text-[#1B4332] text-base leading-tight">
                {currentStep.title}
              </h3>
            )}
            <button
              onClick={finish}
              className="text-gray-400 hover:text-gray-700 -mr-1 -mt-1 flex-shrink-0"
              aria-label={t.close || 'Close'}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          {/* Content */}
          <div className="text-gray-700 text-sm leading-relaxed mb-4 whitespace-pre-line">
            {currentStep.content}
          </div>
          {/* Progress + Actions */}
          <div className="flex items-center justify-between pt-3 border-t border-gray-100">
            <span className="text-xs text-gray-500 font-medium tabular-nums">
              {stepIdx + 1} / {steps.length}
            </span>
            <div className="flex items-center gap-2">
              {stepIdx > 0 && (
                <button
                  onClick={() => setStepIdx(stepIdx - 1)}
                  className="text-gray-600 text-xs hover:text-[#1B4332] flex items-center gap-1 px-2 py-1.5"
                >
                  <ArrowLeft className="w-3 h-3" /> {t.back || 'Back'}
                </button>
              )}
              {stepIdx < steps.length - 1 ? (
                <>
                  <button
                    onClick={finish}
                    className="text-gray-400 text-xs hover:text-gray-700 px-2 py-1.5"
                  >
                    {t.skip || 'Skip'}
                  </button>
                  <button
                    onClick={() => setStepIdx(stepIdx + 1)}
                    className="bg-[#1B4332] text-white text-xs font-medium px-4 py-2 rounded-md hover:bg-[#143528] flex items-center gap-1.5"
                  >
                    {t.next || 'Next'} <ArrowRight className="w-3 h-3" />
                  </button>
                </>
              ) : (
                <button
                  onClick={finish}
                  className="bg-[#1B4332] text-white text-xs font-medium px-4 py-2 rounded-md hover:bg-[#143528]"
                >
                  {t.last || 'Done'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminTour;
